import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import CheckIcon from '@mui/icons-material/Check'
import DoneAllIcon from '@mui/icons-material/DoneAll'
import RefreshIcon from '@mui/icons-material/Refresh'
import dayjs from 'dayjs'
import { useSnackbar } from 'notistack'
import { PageHeader } from '@/components/common/PageHeader'
import { KpiCard } from '@/components/common/KpiCard'
import { TabelaAcao, ColunaTabelaAcao } from '@/components/common/TabelaAcao'
import { EstadoDeLista } from '@/components/common/EstadoDeLista'
import { ReuniaoDialog } from '@/components/agenda/ReuniaoDialog'
import { reuniaoService } from '@/services/reuniaoService'
import { resumoService } from '@/services/resumoService'
import { ReunioesResumo } from '@/types/resumos'
import { Reuniao, StatusReuniao, transicoesValidas } from '@/types/reuniao'
import { authStore } from '@/store/authStore'
import { useSearchParams } from 'react-router-dom'

/**
 * CONFIRMADA chegou no backend com o A06 (V37), mas o tipo `StatusReuniao` do frontend
 * ainda não a declara — o alinhamento das opções é condição de integração do A07
 * (ReuniaoDialog). Por isso os mapas abaixo são `Record<string, …>` com fallback: a
 * Agenda não pode quebrar quando o status novo aparecer na listagem.
 */
const STATUS_LABEL: Record<string, string> = {
  AGENDADA: 'Agendada',
  CONFIRMADA: 'Confirmada',
  REALIZADA: 'Realizada',
  CANCELADA: 'Cancelada',
  REAGENDADA: 'Reagendada',
}

const STATUS_COLOR: Record<
  string,
  'default' | 'primary' | 'success' | 'warning' | 'error' | 'info'
> = {
  AGENDADA: 'info',
  CONFIRMADA: 'primary',
  REALIZADA: 'success',
  CANCELADA: 'error',
  REAGENDADA: 'warning',
}

type StatusFiltro = 'TODOS' | 'AGENDADA' | 'CONFIRMADA' | 'REALIZADA' | 'CANCELADA' | 'REAGENDADA'

/** Formato veio com o A06 (V37); reuniões anteriores a ele não têm — aí é "—". */
const formatoDe = (r: Reuniao): string => {
  if (r.formato === 'ONLINE') return 'Online'
  if (r.formato === 'PRESENCIAL') return 'Presencial'
  return '—'
}

/** Origem da reunião: primeiro contato da jornada, vinculada a uma reserva ou avulsa. */
const origemDe = (r: Reuniao): string =>
  r.primeiroContato ? 'Primeiro contato' : r.oportunidadeId ? 'Reserva' : 'Avulsa'

/**
 * Agenda (Figma 26:2, Onda 2 / A04): KPIs Reuniões / Agendadas / Confirmadas / Realizadas
 * de `GET /v1/reunioes/resumo` (A03 — mesma fonte da listagem) e tabela padrão
 * Empresa · Data e hora · Formato · Origem · Status · Ação.
 *
 * A Agenda é evidência, não move o funil (decisão 6.4): marcar Realizada aqui nunca
 * transiciona reserva nenhuma.
 */
const AgendaPage: FC = () => {
  const { enqueueSnackbar } = useSnackbar()
  const usuario = authStore((s) => s.usuario)
  const isAdmin = useMemo(
    () => !!usuario?.permissoes?.some((p) => p.toUpperCase() === 'ADMIN'),
    [usuario]
  )

  const [reunioes, setReunioes] = useState<Reuniao[]>([])
  const [loading, setLoading] = useState(true)
  const [erro, setErro] = useState<string | null>(null)
  const [erroStatus, setErroStatus] = useState<number | null>(null)

  // KPIs — mesmo escopo da listagem, sem filtro de tela (o resumo é o agregado inteiro).
  const [resumo, setResumo] = useState<ReunioesResumo | null>(null)
  const [carregandoResumo, setCarregandoResumo] = useState(true)

  const [statusFiltro, setStatusFiltro] = useState<StatusFiltro>('TODOS')
  const [consultorFiltro, setConsultorFiltro] = useState<string>('')
  const [inicioFiltro, setInicioFiltro] = useState<string>('')
  const [fimFiltro, setFimFiltro] = useState<string>('')

  const [dialogOpen, setDialogOpen] = useState(false)
  const [reuniaoEdit, setReuniaoEdit] = useState<Reuniao | null>(null)

  const carregarResumo = useCallback(async () => {
    setCarregandoResumo(true)
    try {
      // fonte: GET /v1/reunioes/resumo (A03) — mesma fonte da tabela abaixo
      setResumo(await resumoService.reunioes())
    } catch {
      // KPIs mostram "—"; a falha de verdade aparece no estado da lista.
      setResumo(null)
    } finally {
      setCarregandoResumo(false)
    }
  }, [])

  const carregar = useCallback(async () => {
    setLoading(true)
    setErro(null)
    setErroStatus(null)
    try {
      const page = await reuniaoService.listar({
        page: 0,
        size: 100,
        status: statusFiltro === 'TODOS' ? undefined : (statusFiltro as StatusReuniao),
        consultorId: isAdmin && consultorFiltro ? consultorFiltro : undefined,
        // Backend recebe LocalDateTime (sem fuso): `toISOString()` deslocaria a janela em 3h.
        inicio: inicioFiltro ? dayjs(inicioFiltro).startOf('day').format('YYYY-MM-DDTHH:mm:ss') : undefined,
        fim: fimFiltro ? dayjs(fimFiltro).endOf('day').format('YYYY-MM-DDTHH:mm:ss') : undefined,
      })
      setReunioes(page.content ?? [])
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Falha ao carregar reuniões')
      setErroStatus((e as { status?: number })?.status ?? null)
      setReunioes([])
    } finally {
      setLoading(false)
    }
  }, [statusFiltro, consultorFiltro, inicioFiltro, fimFiltro, isAdmin])

  useEffect(() => {
    void carregarResumo()
  }, [carregarResumo])

  useEffect(() => {
    void carregar()
  }, [carregar])

  // Lista única de consultores para o filtro (admin)
  const consultoresDisponiveis = useMemo(() => {
    const map = new Map<string, string>()
    for (const r of reunioes) {
      if (r.consultorId) map.set(r.consultorId, r.consultorNome ?? r.consultorId)
    }
    return Array.from(map.entries()).map(([id, nome]) => ({ id, nome }))
  }, [reunioes])

  /**
   * Deep links da Central de Pendências (L7 / Q40: "reunião → Agenda no evento"):
   * `?reuniao=<id>` abre a própria reunião (pelo GET escopado — de outro consultor não abre,
   * só avisa); `?novaReuniao=<reservaId>` abre "Nova reunião" já vinculada à reserva cuja
   * reunião o back-office solicitou (o POST valida a carteira).
   */
  const [searchParams] = useSearchParams()
  const reuniaoAlvo = searchParams.get('reuniao')
  const novaReuniaoReserva = searchParams.get('novaReuniao')
  const [prefill, setPrefill] = useState<{ oportunidadeId: string } | undefined>()
  const [reuniaoAlvoFalhou, setReuniaoAlvoFalhou] = useState(false)

  useEffect(() => {
    if (!reuniaoAlvo) return
    let ativo = true
    setReuniaoAlvoFalhou(false)
    reuniaoService
      .obter(reuniaoAlvo)
      .then((r) => {
        if (!ativo) return
        setPrefill(undefined)
        setReuniaoEdit(r)
        setDialogOpen(true)
      })
      .catch(() => {
        if (ativo) setReuniaoAlvoFalhou(true)
      })
    return () => {
      ativo = false
    }
  }, [reuniaoAlvo])

  useEffect(() => {
    if (!novaReuniaoReserva || reuniaoAlvo) return
    setReuniaoEdit(null)
    setPrefill({ oportunidadeId: novaReuniaoReserva })
    setDialogOpen(true)
  }, [novaReuniaoReserva, reuniaoAlvo])

  const handleNovo = () => {
    setReuniaoEdit(null)
    setPrefill(undefined)
    setDialogOpen(true)
  }

  const handleEditar = (r: Reuniao) => {
    setReuniaoEdit(r)
    setPrefill(undefined)
    setDialogOpen(true)
  }

  /**
   * Transições rápidas da linha (A15 / Figma 26:172): `Confirmar` (AGENDADA/REAGENDADA →
   * CONFIRMADA) e `Marcar realizada` (→ REALIZADA). Vão pelo PATCH /status do A06 — é ele
   * quem valida a transição e o escopo; a REALIZADA continua sendo marcada aqui, na Agenda,
   * porque o funil só avança com reunião ocorrida (6.4).
   */
  const handleStatus = async (r: Reuniao, status: StatusReuniao) => {
    try {
      await reuniaoService.atualizarStatus(r.id, { status })
      enqueueSnackbar(
        status === 'CONFIRMADA' ? 'Reunião confirmada' : 'Reunião marcada como realizada',
        { variant: 'success' }
      )
      await carregar()
      await carregarResumo()
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : 'Falha ao atualizar o status', {
        variant: 'error',
      })
    }
  }

  const podeIr = (r: Reuniao, status: StatusReuniao) =>
    r.status !== status && transicoesValidas(r.status).includes(status)

  const handleDeletar = async (r: Reuniao) => {
    if (!window.confirm(`Remover a reunião "${r.titulo}"?`)) return
    try {
      await reuniaoService.deletar(r.id)
      enqueueSnackbar('Reunião removida', { variant: 'success' })
      await carregar()
      await carregarResumo()
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : 'Falha ao remover reunião', {
        variant: 'error',
      })
    }
  }

  // Tabela do Figma: Empresa · Data e hora · Formato · Origem · Status · Ação.
  const colunas: ColunaTabelaAcao<Reuniao>[] = [
    {
      titulo: 'Empresa',
      render: (r) => (
        <Box>
          <Typography variant="body2" fontWeight={600}>
            {r.clienteNome ?? '—'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {r.titulo}
            {r.local ? ` · ${r.local}` : ''}
          </Typography>
        </Box>
      ),
    },
    {
      titulo: 'Data e hora',
      render: (r) => {
        const ini = dayjs(r.dataInicio)
        const fim = dayjs(r.dataFim)
        const mesmoDia = ini.isSame(fim, 'day')
        return (
          <Box>
            <Typography variant="body2" fontWeight={600}>
              {ini.format('DD/MM/YYYY')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {ini.format('HH:mm')}
              {' – '}
              {mesmoDia ? fim.format('HH:mm') : fim.format('DD/MM HH:mm')}
            </Typography>
          </Box>
        )
      },
    },
    { titulo: 'Formato', render: formatoDe },
    { titulo: 'Origem', render: origemDe },
    ...(isAdmin
      ? [{ titulo: 'Consultor', render: (r: Reuniao) => r.consultorNome ?? '—' }]
      : []),
    {
      titulo: 'Status',
      render: (r) => (
        <Chip
          label={STATUS_LABEL[r.status] ?? r.status}
          size="small"
          color={STATUS_COLOR[r.status] ?? 'default'}
        />
      ),
    },
    {
      titulo: 'Ação',
      align: 'right',
      largura: 260,
      render: (r) => (
        <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
          {podeIr(r, 'CONFIRMADA') && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<CheckIcon />}
              onClick={(e) => {
                e.stopPropagation()
                void handleStatus(r, 'CONFIRMADA')
              }}
            >
              Confirmar
            </Button>
          )}
          {podeIr(r, 'REALIZADA') && (
            <Button
              size="small"
              variant="text"
              startIcon={<DoneAllIcon />}
              onClick={(e) => {
                e.stopPropagation()
                void handleStatus(r, 'REALIZADA')
              }}
            >
              Marcar realizada
            </Button>
          )}
          <Tooltip title="Remover">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation()
                void handleDeletar(r)
              }}
            >
              <DeleteIcon fontSize="small" color="error" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ]

  return (
    <Box>
      <PageHeader
        title="Agenda"
        subtitle={
          isAdmin
            ? 'Reuniões da equipe — visão completa'
            : 'Suas reuniões com clientes — a Agenda é evidência, o funil vive no Pipeline'
        }
        acao={
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleNovo}>
            Nova reunião
          </Button>
        }
      />

      {/* Aviso do Figma (37:2, "Nova reunião"): a integração com o Outlook não existe ainda. */}
      <Alert severity="info" role="note" sx={{ mb: 2 }}>
        O convite será enviado por e-mail (integração Outlook em avaliação). Reuniões
        presenciais exigem o local.
      </Alert>

      {reuniaoAlvoFalhou && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          A reunião indicada não foi encontrada na sua agenda.
        </Alert>
      )}

      {/* KPIs — 1º card escuro (convenção da página, Onda 0). */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          {/* fonte: GET /v1/reunioes/resumo (total) */}
          <KpiCard
            label="Reuniões"
            valor={resumo?.total ?? '—'}
            legenda="No seu escopo"
            variante="escura"
            carregando={carregandoResumo}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          {/* fonte: GET /v1/reunioes/resumo (agendadas) */}
          <KpiCard
            label="Agendadas"
            valor={resumo?.agendadas ?? '—'}
            carregando={carregandoResumo}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          {/* fonte: GET /v1/reunioes/resumo (confirmadas — status CONFIRMADA, V37/A06) */}
          <KpiCard
            label="Confirmadas"
            valor={resumo?.confirmadas ?? '—'}
            carregando={carregandoResumo}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          {/* fonte: GET /v1/reunioes/resumo (realizadas) */}
          <KpiCard
            label="Realizadas"
            valor={resumo?.realizadas ?? '—'}
            carregando={carregandoResumo}
          />
        </Grid>
      </Grid>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFiltro}
              label="Status"
              onChange={(e) => setStatusFiltro(e.target.value as StatusFiltro)}
            >
              <MenuItem value="TODOS">Todos</MenuItem>
              {Object.keys(STATUS_LABEL).map((s) => (
                <MenuItem key={s} value={s}>
                  {STATUS_LABEL[s]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {isAdmin && (
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>Consultor</InputLabel>
              <Select
                value={consultorFiltro}
                label="Consultor"
                onChange={(e) => setConsultorFiltro(e.target.value)}
              >
                <MenuItem value="">Todos</MenuItem>
                {consultoresDisponiveis.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.nome}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <TextField
            size="small"
            type="date"
            label="De"
            value={inicioFiltro}
            onChange={(e) => setInicioFiltro(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            size="small"
            type="date"
            label="Até"
            value={fimFiltro}
            onChange={(e) => setFimFiltro(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />

          <Box sx={{ flex: 1 }} />

          <IconButton onClick={() => void carregar()} disabled={loading} aria-label="Recarregar">
            <RefreshIcon />
          </IconButton>
        </Stack>
      </Paper>

      {erro ? (
        <EstadoDeLista
          estado={erroStatus === 403 ? 'semPermissao' : 'erro'}
          {...(erroStatus !== 403 && {
            mensagem: 'Não foi possível carregar as reuniões',
            detalhe: erro,
            acao: { label: 'Tentar novamente', onClick: () => void carregar() },
          })}
        />
      ) : loading ? (
        <EstadoDeLista estado="carregando" mensagem="Carregando reuniões…" />
      ) : reunioes.length === 0 ? (
        <EstadoDeLista
          estado="vazio"
          mensagem="Nenhuma reunião encontrada"
          detalhe='Clique em "Nova reunião" para agendar.'
          acao={{ label: 'Nova reunião', onClick: handleNovo }}
        />
      ) : (
        <TabelaAcao
          aria-label="Reuniões"
          colunas={colunas}
          linhas={reunioes}
          chaveDaLinha={(r) => r.id}
          onAbrir={handleEditar}
        />
      )}

      <ReuniaoDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        reuniao={reuniaoEdit}
        prefill={prefill}
        onSaved={() => {
          void carregar()
          void carregarResumo()
        }}
      />
    </Box>
  )
}

export default AgendaPage
